'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { colors } from '../../../constants/colors'
import UserStorage from '../../../storage/UserStorage/UserStorage'
import { breakpoints, tabletDesktopView } from '../../breakPoints'
import { MobileView } from '../../projectComponents'
import { RightModal } from '../modals/RightModal'
import { MainMenuWindow } from '../modalsContent/MainMenuWindow'
import { HeaderFilterMenu } from './HeaderFilterMenu'
import { ProductSearch } from './ProductSearch'

type Props = {
  isMobile?: boolean
}

export const MainHeader = observer(({ isMobile }: Props) => {
  const pathname = usePathname()
  const userStore = useContext(UserStorage)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const isAuth = isHydrated && Boolean(userStore.userId)
  const isAdmin = isAuth && userStore.user?.UserRole === 'Admin'
  const lastPathname = pathname.replace(/^\//, '')

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(prev => !prev)
  }

  return (
    <>
      <Header isMobile={isMobile}>
        <DesktopLogoLink href={'/'}>
          <DesktopLogo alt={'Allsharing'} src={'/images/allsharinglogo.svg'} />
        </DesktopLogoLink>
        <ProductSearch onClickFilter={handleFilterModalOpen} />

        <AddProductLink href={isAuth ? '/add' : '/register'}>
          <PlusIcon aria-hidden={'true'} fill={'none'} viewBox={'0 0 17 17'}>
            <path
              d={'M10.5 8.25H8.25M8.25 8.25H6M8.25 8.25V6M8.25 8.25V10.5'}
              stroke={'currentColor'}
              strokeLinecap={'round'}
              strokeWidth={'1.5'}
            />
            <path
              d={
                'M4.5 1.75337C5.60315 1.11523 6.88395 0.75 8.25 0.75C12.3921 0.75 15.75 4.10786 15.75 8.25C15.75 12.3921 12.3921 15.75 8.25 15.75C4.10786 15.75 0.75 12.3921 0.75 8.25C0.75 6.88395 1.11523 5.60315 1.75337 4.5'
              }
              stroke={'currentColor'}
              strokeLinecap={'round'}
              strokeWidth={'1.5'}
            />
          </PlusIcon>
          <AddProductText>Разместить объявление</AddProductText>
        </AddProductLink>

        {isAuth && (
          <MobileMenuView>
            <MenuActionButton
              aria-label={'Открыть меню'}
              onClick={() => setIsModalOpen(prev => !prev)}
              type={'button'}
            >
              <img alt={''} src={'/images/buttons/menu-button.svg'} />
            </MenuActionButton>
          </MobileMenuView>
        )}

        <DesktopActions>
          {isAuth ? (
            <>
              {isAdmin && <AdminLink href={'/admin'}>Админ</AdminLink>}
              <ActionLink href={'/orders'} aria-label={'Корзина'}>
                <img alt={''} src={'/images/buttons/cart.svg'} />
              </ActionLink>
              <ActionLink href={'/profile'} aria-label={'Профиль'}>
                <img alt={''} src={'/images/buttons/profile-linear.svg'} />
              </ActionLink>
              <MenuActionButton
                aria-label={'Открыть меню'}
                onClick={() => setIsModalOpen(prev => !prev)}
                type={'button'}
              >
                <img alt={''} src={'/images/buttons/menu-button.svg'} />
              </MenuActionButton>
            </>
          ) : (
            <>
              <StyledLinkHeader href={lastPathname === 'login' ? '/register' : '/login'}>
                {lastPathname === 'login' ? 'Регистрация' : 'Войти'}
              </StyledLinkHeader>
            </>
          )}
        </DesktopActions>
      </Header>
      <RightModal setViewModal={setIsModalOpen} viewModal={isModalOpen}>
        <MainMenuWindow handleModalClose={handleModalClose} />
      </RightModal>
      <HeaderFilterMenu
        onToggle={handleFilterModalOpen}
        open={isFilterModalOpen}
        setOpen={setIsFilterModalOpen}
      />
    </>
  )
})

const Header = styled.div<Props>`
  position: relative;
  top: 0;
  z-index: 2200;
  width: min(100%, 1280px);
  margin-inline: auto;
  min-height: 50px;
  display: flex;
  gap: clamp(18px, 2.3vw, 40px);
  align-items: center;
  padding: 4px clamp(28px, 3.5vw, 56px);
  border-radius: 0 0 24px 24px;
  box-shadow: 0 8px 14px 0 #5e5e5e12;
  background-color: ${colors.white};

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    width: 100%;
    min-height: 50px;
    gap: 20px;
    padding: 17px 20px;
    border-radius: 0 0 20px 20px;
  }

  ${({ isMobile }) => isMobile === false && tabletDesktopView}
`

const DesktopLogoLink = styled(Link)`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: none;
  }
`

const DesktopLogo = styled.img`
  width: clamp(82px, 7vw, 112px);
  height: 78px;
  object-fit: contain;
`

const AddProductLink = styled(Link)`
  min-width: 0;
  height: 38px;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 16px;
  border-radius: 10px;
  color: ${colors.white};
  background: ${colors.primary};
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: ${colors.primaryActive};
  }

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    min-width: 38px;
    width: 38px;
    height: 38px;
    padding: 0;
    border-radius: 10px;
  }
`

const PlusIcon = styled.svg`
  width: 17px;
  height: 17px;
  flex: 0 0 auto;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    width: 18px;
    height: 18px;
  }
`

const AddProductText = styled.span`
  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: none;
  }
`

const DesktopActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
  margin-left: 10px;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: none;
  }
`

const MobileMenuView = styled(MobileView)`
  display: none;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: auto;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: flex;
  }
`

const ActionLink = styled(Link)`
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #e8f5f5;
  text-decoration: none;

  img {
    width: 20px;
    height: 22px;
    object-fit: contain;
  }
`

const StyledLink = styled(Link)<{ fullWidth?: boolean }>`
  background: #008c8c;
  color: #fff;
  border-radius: 15px;
  padding: 13px 15px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  border: none;
  text-decoration: none;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
  :hover {
    opacity: 0.6;
    transition: all ease-in 0.2s;
  }
`

const AdminLink = styled(StyledLink)`
  min-height: 38px;
  align-items: center;
  padding: 8px 12px;
  border-radius: 10px;
  text-transform: capitalize;
`

const StyledLinkHeader = styled(StyledLink)`
  min-height: 52px;
  align-items: center;
  padding: 12px 28px;
  margin: 0;
  font-size: 15px;

  &:hover {
    background: ${colors.primaryActive};
  }
`

const MenuActionButton = styled.button`
  width: 38px;
  height: 38px;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  padding: 0;
  background: ${colors.lightGray};
  cursor: pointer;

  img {
    width: 18px;
    height: 18px;
  }
`
